/**
 * Generic CSV generation utilities
 * Reusable across different CSV generation scenarios
 */

/**
 * Generate CSV string from row data with generic types
 * @param rows - Array of row objects
 * @param columnOrder - Array of column keys in order
 * @param headerLabels - Map of column keys to header labels
 * @example
 * ```typescript
 * const users = [{ name: 'John', age: 30 }, { name: 'Jane', age: 25 }];
 * const columnOrder = ['name', 'age'] as const;
 * const headerLabels = { name: 'Name', age: 'Age' };
 * const csv = generateCsvString(users, columnOrder, headerLabels);
 * // Output: "Name,Age\r\nJohn,30\r\nJane,25\r\n"
 * ```
 */
export function generateCsvString<T extends Record<string, unknown>>(
  rows: T[],
  columnOrder: Array<keyof T>,
  headerLabels: Record<keyof T, string>,
): string {
  if (rows.length === 0) {
    // Generate header using provided labels
    const headerRow = columnOrder
      .map((column) => headerLabels[column])
      .join(',')
    return headerRow + '\r\n'
  }

  // Header row with provided labels
  const headerRow = columnOrder.map((column) => headerLabels[column]).join(',')

  // Data rows
  const dataRows = rows.map((row) =>
    columnOrder.map((column) => escapeCsvValue(String(row[column]))).join(','),
  )

  return [headerRow, ...dataRows].join('\r\n')
}

/**
 * Generate CSV string without header
 * @param rows - Array of row objects
 * @param columnOrder - Array of column keys in order
 */
export function generateCsvRowsOnly<T extends Record<string, unknown>>(
  rows: T[],
  columnOrder: Array<keyof T>,
): string {
  const dataRows = rows.map((row) =>
    columnOrder.map((column) => escapeCsvValue(String(row[column]))).join(','),
  )

  return dataRows.join('\r\n')
}

/**
 * Generate only CSV header
 * @param columnOrder - Array of column keys in order
 * @param headerLabels - Map of column keys to header labels
 */
export function generateCsvHeaderOnly<T extends Record<string, unknown>>(
  columnOrder: Array<keyof T>,
  headerLabels: Record<keyof T, string>,
): string {
  return columnOrder.map((column) => headerLabels[column]).join(',')
}

/**
 * Escape CSV values for security and compatibility
 * - Prevents Excel formula injection for non-numeric values
 * - Handles commas, quotes, newlines
 */
function escapeCsvValue(value: string): string {
  let escapedValue = value

  // Check if it's a pure number (including negative numbers, decimals, and comma-separated numbers)
  const isNumeric = /^-?(\d{1,3}(,\d{3})*|\d+)(\.\d+)?$/.test(escapedValue)

  // Only prevent Excel formula injection for non-numeric values
  if (!isNumeric && /^[=+\-@]/.test(escapedValue)) {
    escapedValue = `'${escapedValue}`
  }

  // Standard CSV escaping
  if (
    escapedValue.includes(',') ||
    escapedValue.includes('"') ||
    escapedValue.includes('\n') ||
    escapedValue.includes('\r')
  ) {
    escapedValue = `"${escapedValue.replace(/"/g, '""')}"`
  }

  return escapedValue
}
